import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Button, TextView, ChartTooltip } from '../../../../atoms';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DateTime } from 'luxon';
import { useTwilioSrtTokens } from '@api';
import { CommonCard, SkeletonChart } from '@organisms';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { toast } from 'react-toastify';
import CustomizedYAxisTick from '../particles/CustomizedYAxisTick';
import CustomizedXAxisTick from '../particles/CustomizedXAxisTick';

interface callGetSrtTokenUsageProps {
  billingAccount?: BillingAccount;
  dates: { start: DateTime | null; end: DateTime | null };
  TwillioInboundCallCount: number;
  TwillioOutboundCallCount: number;
  TwillioOutboundSmsCount: number;
  TwillioInboundSmsCount: number;
  TwillioOtherCount: number;
}

const TwilioChart: React.FC<callGetSrtTokenUsageProps> = ({
  billingAccount,
  dates,
  TwillioInboundCallCount,
  TwillioOutboundCallCount,
  TwillioOutboundSmsCount,
  TwillioInboundSmsCount,
  TwillioOtherCount
}: callGetSrtTokenUsageProps) => {
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const startDate = dates.start?.toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });

  const endDate = dates.end?.toISO({
    suppressMilliseconds: true,
    includeOffset: false
  });

  const { data: responseSrt, isFetching: isLoading } = useTwilioSrtTokens({
    from: startDate,
    to: endDate,
    billingAccountId: billingAccount?.id
  });

  const chartValues = useMemo(() => {
    if (!responseSrt?.data?.data || !dates.start || !dates.end) return [];

    const counts = responseSrt.data.data;
    const dateMap = new Map<string, { call: number; sms: number; other: number }>();
    counts.forEach((item) => {
      if (item.date) {
        const key = item.date.substring(0, 10);
        const prev = dateMap.get(key) ?? { call: 0, sms: 0, other: 0 };
        dateMap.set(key, {
          call: prev.call + Number(item.callCount ?? 0),
          sms: prev.sms + Number(item.smsCount ?? 0),
          other: prev.other + Number(item.otherCount ?? 0)
        });
      }
    });

    const values: { date: string | null; callCount: number; smsCount: number; otherCount: number }[] = [];
    let day = dates.start;
    const lastDay = dates.end;
    while (day <= lastDay) {
      const key = day.toFormat('yyyy-MM-dd');
      const bucket = dateMap.get(key) ?? { call: 0, sms: 0, other: 0 };
      values.push({
        date: day.toISO(),
        callCount: bucket.call,
        smsCount: bucket.sms,
        otherCount: bucket.other
      });
      day = day.plus({ day: 1 });
    }
    return values;
  }, [responseSrt, dates.start, dates.end]);

  const downloadLogs = async () => {
    setExportLoading(true);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE;

    const queryString = `
    ${billingAccount?.id ? `&billingAccountId=${billingAccount?.id}` : ``}
    ${`&markupType=twillio`}
    ${startDate ? `&startDate=${startDate}` : ``}
    ${endDate ? `&endDate=${endDate}` : ``}`;

    const urlApi = `${apiBase}/twilio-usage/csv-download?downloadCsv=true&${queryString}`;

    const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
    const hour = Number(zone[0]);
    const mint = Number(zone[1]);
    const totalMint: number = hour * 60 + mint;

    const fetchOptions: RequestInit = {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        timezone: `${totalMint}`
      },
      method: 'GET'
    };

    const response: Response = await fetch(urlApi, fetchOptions);

    if (response.status == 200) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Twillio ${DateTime.now().toFormat('LLL dd yyyy, hh:mm:ss')}.csv`
      );
      document.body.appendChild(link);
      link.click();
    } else {
      toast('Something went wrong');
    }
    setExportLoading(false);
  };

  return (
    <Box mt={2}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <TextView $fontSize={26} $fontWeight={700}>
          Twilio
        </TextView>
        <Button
          label="Export"
          onClick={downloadLogs}
          size="small"
          sx={{ mr: 2 }}
          disabled={exportLoading}
        />
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          gridAutoRows: '1fr',
          mt: 2,
          mb: 2
        }}
      >
        <CommonCard
          title="SMS"
          count={TwillioOutboundSmsCount + TwillioInboundSmsCount}
          icon={<SmsOutlinedIcon />}
          accent="#086BDE"
        />
        <CommonCard
          title="Call"
          count={TwillioInboundCallCount + TwillioOutboundCallCount}
          icon={<CallOutlinedIcon />}
          accent="#F5CB5C"
        />
        <CommonCard
          title="Other"
          count={TwillioOtherCount}
          icon={<MoreHorizOutlinedIcon />}
          accent="#fe4a49"
        />
      </Box>
      {isLoading && !responseSrt?.data ? (
        <SkeletonChart height={300} />
      ) : (
      <Box
        mt={1}
        sx={{
          backgroundColor: 'rgba(217, 119, 6, 0.06)',
          border: '1px solid rgba(217, 119, 6, 0.15)',
          pt: 2,
          pb: 2,
          borderRadius: 2,
          height: 300
        }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartValues}
            margin={{
              top: 20,
              right: 20,
              left: 0,
              bottom: 5
            }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis interval={0} dataKey="date" tick={(props) => CustomizedXAxisTick(props)} />
            <YAxis tick={(props) => CustomizedYAxisTick(props)} />
            <Tooltip
              itemSorter={(item) => {
                switch (item.dataKey) {
                  case 'smsCount':
                    return 0;
                  case 'callCount':
                    return 1;
                  case 'otherCount':
                    return 2;
                  default:
                    return 3;
                }
              }}
              content={
                <ChartTooltip
                  nameMap={{ smsCount: 'SMS', callCount: 'Call', otherCount: 'Other' }}
                  showTotal
                  filterZero
                />
              }
              cursor={{ fill: 'rgba(43, 34, 88, 0.06)' }}
            />
            <Bar dataKey="smsCount" name="SMS" stackId="a" fill="#086BDE" />
            <Bar dataKey="callCount" name="Call" stackId="a" fill="#F5CB5C" />
            <Bar dataKey="otherCount" name="Other" stackId="a" fill="#fe4a49" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      )}
    </Box>
  );
};

export default TwilioChart;
